import type { JobTail, RunLogEntry, RunProgress } from "@/lib/jobs-types";

// Pure tail folding + liveness rules for the run-log panel (sc-114197). No React, no timers, no
// `Date.now()`: the caller supplies the clock value, so every rule is testable with plain values.

/**
 * The worker's framework heartbeat interval — mirrors `RunProgressOptions.HeartbeatInterval`
 * (`dotnet/Conduit/Trillion.Conduit.Jobs/Observability/RunProgressOptions.cs`). Change both together.
 */
export const HEARTBEAT_INTERVAL_MS = 15_000;
/** A heartbeat older than this (strictly) raises "Worker may be gone": 3 × the heartbeat interval. */
export const HEARTBEAT_STALE_AFTER_MS = 3 * HEARTBEAT_INTERVAL_MS;
/** A caught-up view with no new entry for this long shows the informational quiet line. */
export const QUIET_NOTICE_AFTER_MS = 10_000;

export interface TailView {
  entries: RunLogEntry[];
  /** Highest log_id delivered; the next request's `after`. */
  cursor: number;
  progress: RunProgress | null;
  /** Sticky: once a page reports `finished`, the view stays finished. */
  finished: boolean;
  orphaned: boolean;
  /** The last page returned fewer rows than the limit, so no persisted row remains beyond the cursor. */
  caughtUp: boolean;
}

export const EMPTY_TAIL: TailView = {
  entries: [],
  cursor: 0,
  progress: null,
  finished: false,
  orphaned: false,
  caughtUp: false,
};

/**
 * Folds one tail page into the view by cursor. Entries at or below the cursor (an overlapping page) are
 * dropped; with nothing new the `entries` array keeps its identity. The cursor never regresses.
 */
export function applyTailPage(prev: TailView, page: JobTail, limit: number): TailView {
  const fresh = page.entries.filter((entry) => entry.log_id > prev.cursor);
  return {
    entries: fresh.length ? [...prev.entries, ...fresh] : prev.entries,
    cursor: Math.max(prev.cursor, page.next_cursor),
    progress: page.progress,
    finished: prev.finished || page.finished,
    orphaned: page.orphaned,
    caughtUp: page.entries.length < limit,
  };
}

export type WorkerNotice =
  | { reason: "orphaned" }
  | { reason: "heartbeat"; staleForSeconds: number };

export interface TailNotices {
  quietForSeconds: number | null;
  workerMayBeGone: WorkerNotice | null;
}

/**
 * Derives the quiet line and the dead-worker notice from the view as of `nowMs` (the time of the last
 * successful response). Liveness keys on `orphaned` or the framework heartbeat — never on recent output.
 *
 * Both clock-derived lines are judged only on a caught-up view: while full pages remain beyond the
 * cursor, neither the newest delivered line nor a just-finished run's frozen heartbeat describes the
 * worker. `orphaned` is never gated. The two lines are independent — a dead worker's run is quiet too.
 */
export function tailNotices(view: TailView, nowMs: number): TailNotices {
  if (view.finished) return { quietForSeconds: null, workerMayBeGone: null };

  let workerMayBeGone: WorkerNotice | null = null;
  if (view.orphaned) {
    workerMayBeGone = { reason: "orphaned" };
  } else if (view.caughtUp && view.progress) {
    const age = nowMs - Date.parse(view.progress.heartbeat_at);
    if (age > HEARTBEAT_STALE_AFTER_MS) {
      workerMayBeGone = { reason: "heartbeat", staleForSeconds: Math.floor(age / 1000) };
    }
  }

  let quietForSeconds: number | null = null;
  if (view.caughtUp) {
    const last = view.entries[view.entries.length - 1];
    const reference = last ? last.logged_at : (view.progress?.started_at ?? null);
    if (reference) {
      const quiet = nowMs - Date.parse(reference);
      if (quiet >= QUIET_NOTICE_AFTER_MS) quietForSeconds = Math.floor(quiet / 1000);
    }
  }

  return { quietForSeconds, workerMayBeGone };
}

/**
 * Pages one finished run's complete log by cursor, for replay (sc-114198). Each page is folded with
 * `applyTailPage`, and the load stops as soon as the log is exhausted as of that read: a page reporting
 * `finished`, a short page (nothing lies beyond its cursor), or a page that did not advance the cursor.
 * So a log of N rows costs at most ⌊N / limit⌋ + 1 requests, and nothing polls.
 *
 * `final` is true only when the tail reported `finished`: a short page with `finished: false` still stops
 * the load, but one racing row may yet land inside the tail's grace window, so the result is flagged
 * rather than silently complete. The loader never waits or re-requests for it.
 *
 * Resolves `null` when `isCancelled()` turns true after a page (nothing more is requested). A rejected
 * page rejects the load: no retry, no partial result.
 */
export async function loadCompleteLog(
  fetchPage: (after: number) => Promise<JobTail>,
  limit: number,
  isCancelled: () => boolean = () => false
): Promise<{ entries: RunLogEntry[]; final: boolean } | null> {
  let view = EMPTY_TAIL;
  for (;;) {
    const after = view.cursor;
    const page = await fetchPage(after);
    if (isCancelled()) return null;
    view = applyTailPage(view, page, limit);
    if (view.finished || view.caughtUp || view.cursor <= after) {
      return { entries: view.entries, final: view.finished };
    }
  }
}
